// UpdateGroupModal: create or update a knowledge group.
//
// Purpose
// - Presents a modal form to add a new knowledge group (with parent_id) or edit an existing one.
// - Uses redux actions to persist changes and calls confirmHandler on success so parents can refresh state.
//
// Behavior
// - In create mode (editMode: false): sends name, topic, and parent_id (null for top-level) to createKnowledgeGroup.
// - In edit mode (editMode: true): updates the group identified by _id with name and topic via updateKnowledgeGroup.
// - Submit button is disabled until both Display Name and Topic are non-empty (after trim).
// - Inputs and buttons have tab order and label associations for accessibility.
//
// Usage example
// <UpdateGroupModal
//   show={isOpen}
//   editMode={false}
//   parent_id={parentId /* null for top-level */}
//   onHide={() => setOpen(false)}
//   confirmHandler={() => {
//     setOpen(false)
//     reload()
//   }}
// />
//
// In edit mode
// <UpdateGroupModal
//   show={isOpen}
//   editMode
//   _id={group._id}
//   name={group.name}
//   topic={group.topic}
//   onHide={() => setOpen(false)}
//   confirmHandler={() => {
//     setOpen(false)
//     reload()
//   }}
// />

import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, OutlinedInput, Stack, Typography } from '@mui/material'

import {
  createKnowledgeGroup,
  updateKnowledgeGroup,
} from '@/store/knowledge/actions'
import BasicModal from '../Dialog'
import { KB_PATHS } from '@/routes/Support/Constants'
import UserGuideTooltip from '@/components/UI/Tooltip/UserGuideTooltip'

/**
 * Props for UpdateGroupModal
 *
 * show        - Whether the modal is visible.
 * _id         - ID of the current group when editing. Ignored in create mode.
 * name        - Existing group name (edit mode only), used to prefill the form.
 * topic       - Existing group topic (edit mode only), used to prefill the form.
 * editMode    - If true, updates an existing group; otherwise creates a new one.
 * parent_id   - Parent group ID when creating a new group (null for top-level). Ignored in edit mode.
 * confirmHandler - Called with the API response on success so the parent can refresh state.
 * onHide      - Callback to close the modal.
 *
 * Notes
 * - Form fields (Display Name, Topic) are kept in sync with incoming props when they change.
 */
interface CurrentGroupProps {
  show: boolean
  _id?: string
  name?: string
  topic?: string
  editMode?: boolean
  parent_id?: string | null
  confirmHandler: (res: any) => void
  onHide: () => void
}

export default function UpdateGroupModal(props: CurrentGroupProps) {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [moduleName, setModuleName] = useState<string | null>(
    props.editMode ? props.name : '',
  )
  const [moduleTopic, setModuleTopic] = useState<string | null>(
    props.editMode ? props.topic : '',
  )

  useEffect(() => {
    // Keep form values in sync with props when modal opens or selection changes
    if (props.editMode) {
      setModuleName(props.name ?? '')
      setModuleTopic(props.topic ?? '')
    } else {
      // In create mode, reset fields when parent or show state changes
      setModuleName('')
      setModuleTopic('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editMode, props.name, props.topic, props.show, props.parent_id])

  const confirmRenameHandler = async (): Promise<void> => {
    try {
      let res: any
      if (props.editMode) {
        res = await dispatch(
          updateKnowledgeGroup(
            {
              name: moduleName,
              topic: moduleTopic,
            },
            props._id,
          ),
        )
      } else {
        res = await dispatch(
          createKnowledgeGroup({
            name: moduleName,
            topic: moduleTopic,
            parent_id: props.parent_id,
          }),
        )
      }
      props.confirmHandler(res)
    } catch (error: any) {}
  }

  const isSubmitEnabled =
    Boolean(moduleName?.trim()) && Boolean(moduleTopic?.trim())

  return (
    <BasicModal
      open={props.show}
      onClose={props.onHide}
      sx={{ width: 480, p: 2, height: 'auto' }}
      title={`${props.name ? t('Update knowledge group') : t('Add a knowledge group')}`}
      showCloseButton
    >
      <Stack
        component="form"
        spacing={2}
        onSubmit={e => {
          e.preventDefault()
          confirmRenameHandler()
        }}
      >
        <div>
          <Typography
            sx={{ mb: 0.5, fontWeight: 600, py: 1 }}
            variant="body3"
            component="label"
            htmlFor="display-name-input"
          >
            {t('Display Name')}
            <UserGuideTooltip
              tooltipText={t(
                'This is the name learners will see in their menus. Use a clear title that helps them understand what part of the knowledge base they’re accessing.',
              )}
              link={KB_PATHS.CREATING_KNOWLEDGE_GROUPS}
              ariaLabel={t('Learn more')}
            />
          </Typography>
          <OutlinedInput
            id="display-name-input"
            value={moduleName || ''}
            onChange={e => setModuleName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            tabIndex={1}
            placeholder={t('Eg: Chapter 1 - Human Development')}
          />
          <Typography variant="caption" color="textGrey" className="tw-p-1">
            {t(
              'This is what learners will see in the chat or menus. Use a clear and friendly name.',
            )}
          </Typography>
          <Typography
            sx={{ mb: 0.5, fontWeight: 600, py: 1 }}
            variant="body3"
            component="label"
            htmlFor="topic-input"
          >
            {t('Topic')}
            <UserGuideTooltip
              tooltipText={t(
                'Enter a short, specific phrase that captures the topic of this group.This helps the AI tutor understand what the content is about.',
              )}
              link={KB_PATHS.CREATING_KNOWLEDGE_GROUPS}
              ariaLabel={t('Learn more')}
            />
          </Typography>
          <OutlinedInput
            id="topic-input"
            value={moduleTopic || ''}
            onChange={e => setModuleTopic(e.target.value)}
            size="small"
            fullWidth
            tabIndex={2}
            placeholder={t('Eg: Human Behavior and growth')}
          />
          <Typography variant="caption" color="textGrey" className="tw-p-1">
            {t(
              'Enter a short, specific phrase that captures what this group is about.',
            )}
          </Typography>
        </div>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" pt={1}>
          <Button variant="outlined" onClick={props.onHide} tabIndex={3}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isSubmitEnabled}
            tabIndex={4}
          >
            {props.name ? t('Update Group') : t('Create Group')}
          </Button>
        </Stack>
      </Stack>
    </BasicModal>
  )
}
